import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'general',     label: '💬 General Feedback' },
  { value: 'application', label: '📄 Application Process' },
  { value: 'service',     label: '🏢 Department Service' },
  { value: 'technical',   label: '💻 Technical Issue' },
  { value: 'suggestion',  label: '💡 Suggestion' },
  { value: 'complaint',   label: '⚠️ Complaint' },
];

export default function CitizenFeedback() {
  const [form, setForm] = useState({
    category: 'general',
    subject: '',
    message: '',
    rating: 0,
  });
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [tab, setTab]                 = useState('new'); // 'new' | 'history'

  useEffect(() => {
    api.get('/feedback/my').then(r => setMyFeedbacks(r.feedbacks || [])).catch(() => {});
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.subject.trim()) return toast.error('Please enter subject');
    if (!form.message.trim()) return toast.error('Please write your feedback');
    if (form.rating === 0) return toast.error('Please give a rating');
    setSubmitting(true);
    try {
      await api.post('/feedback', form);
      toast.success('Feedback submitted! Thank you.');
      setSubmitted(true);
      setForm({ category: 'general', subject: '', message: '', rating: 0 });
      // Reload history
      const r = await api.get('/feedback/my');
      setMyFeedbacks(r.feedbacks || []);
      setTimeout(() => { setSubmitted(false); setTab('history'); }, 1500);
    } catch {
      toast.error('Submit failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const StarRating = () => (
    <div className="flex gap-2">
      {[1,2,3,4,5].map(star => (
        <button key={star} type="button"
          onClick={() => f('rating', star)}
          className={`text-3xl transition-all hover:scale-110 ${form.rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}>
          ★
        </button>
      ))}
      <span className="ml-2 text-sm text-gray-500 self-center">
        {form.rating === 0 ? 'Select rating' :
         form.rating === 1 ? 'Poor' :
         form.rating === 2 ? 'Fair' :
         form.rating === 3 ? 'Good' :
         form.rating === 4 ? 'Very Good' : 'Excellent'}
      </span>
    </div>
  );

  const statusColors = {
    pending:    'bg-yellow-50 text-yellow-700 border-yellow-200',
    reviewed:   'bg-blue-50 text-blue-700 border-blue-200',
    resolved:   'bg-green-50 text-green-700 border-green-200',
    closed:     'bg-gray-50 text-gray-500 border-gray-200',
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">📝 Feedback</h1>
        <p className="text-sm text-gray-500 mt-1">Share your experience or report issues</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
        <button onClick={() => setTab('new')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all
            ${tab === 'new' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
          ✍️ New Feedback
        </button>
        <button onClick={() => setTab('history')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all
            ${tab === 'history' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
          📋 My Feedbacks
          {myFeedbacks.length > 0 && (
            <span className="ml-1.5 bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
              {myFeedbacks.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'new' && (
        <div className="card max-w-2xl">
          {submitted ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl font-bold text-green-700">Thank You!</h3>
              <p className="text-gray-500 mt-2">Your feedback has been submitted successfully.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Category */}
              <div>
                <label className="form-label">Category <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CATEGORIES.map(cat => (
                    <button key={cat.value} type="button"
                      onClick={() => f('category', cat.value)}
                      className={`p-2.5 rounded-xl border text-xs font-medium text-left transition-all
                        ${form.category === cat.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-blue-200'}`}>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="form-label">Overall Rating <span className="text-red-500">*</span></label>
                <StarRating />
              </div>

              {/* Subject */}
              <div>
                <label className="form-label">Subject <span className="text-red-500">*</span></label>
                <input type="text" className="form-input" placeholder="Brief subject of your feedback"
                  value={form.subject} onChange={e => f('subject', e.target.value)} maxLength={100} />
                <p className="text-xs text-gray-400 text-right mt-0.5">{form.subject.length}/100</p>
              </div>

              {/* Message */}
              <div>
                <label className="form-label">Message <span className="text-red-500">*</span></label>
                <textarea className="form-input" rows={5}
                  placeholder="Please describe your feedback, suggestion, or complaint in detail..."
                  value={form.message} onChange={e => f('message', e.target.value)} maxLength={1000} />
                <p className="text-xs text-gray-400 text-right mt-0.5">{form.message.length}/1000</p>
              </div>

              <button onClick={handleSubmit} disabled={submitting}
                className="btn-primary w-full py-3 text-base">
                {submitting ? '⏳ Submitting...' : '📤 Submit Feedback'}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div>
          {myFeedbacks.length === 0 ? (
            <div className="card text-center py-16">
              <p className="text-5xl mb-3">📭</p>
              <p className="text-lg font-semibold text-gray-700">No Feedbacks Yet</p>
              <p className="text-sm text-gray-400 mt-1">Your submitted feedbacks will appear here</p>
              <button onClick={() => setTab('new')} className="btn-primary mt-4">
                ✍️ Submit Feedback
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {myFeedbacks.map(fb => (
                <div key={fb.id} className="card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-800">{fb.subject}</span>
                        <span className={`text-xs border px-2 py-0.5 rounded-full font-medium capitalize ${statusColors[fb.status] || statusColors.pending}`}>
                          {fb.status || 'Pending'}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
                          {CATEGORIES.find(c => c.value === fb.category)?.label || fb.category}
                        </span>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {[1,2,3,4,5].map(s => (
                          <span key={s} className={`text-base ${fb.rating >= s ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{fb.message}</p>
                      {fb.admin_reply && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                          <p className="text-xs font-semibold text-blue-700 mb-1">💬 Admin Reply:</p>
                          <p className="text-sm text-blue-800">{fb.admin_reply}</p>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0">
                      {fb.created_at ? new Date(fb.created_at).toLocaleDateString('en-IN') : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}